import { useState, useEffect } from 'react';
import { auth } from '../../firebase';
import { useRole, ROLES } from '../../hooks/useRole';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function UserRoleManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isSuperAdmin } = useRole();

  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
    }
  }, [isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/users?approved=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch users');
      
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (uid, newRole) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/users/${uid}/role`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) throw new Error('Failed to update role');

      // Update local state
      setUsers(prev => prev.map(user => 
        user.uid === uid ? { ...user, role: newRole } : user
      ));

      alert(`✅ Updated user role to ${newRole}`);
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Error updating user role');
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
        <p className="text-red-700">Only Super Admins can manage user roles.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">User Role Management</h2>
        <p className="text-gray-600 text-sm mt-1">Manage roles for approved users</p>
      </div>
      
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading users...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button 
              onClick={fetchUsers}
              className="mt-2 text-red-600 hover:text-red-700 text-sm underline"
            >
              Retry
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No approved users found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Email</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Current Role</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Change Role</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900">Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.uid} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="font-medium text-gray-900">{user.email}</div>
                      <div className="text-sm text-gray-500">UID: {user.uid}</div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        user.role === ROLES.SUPER_ADMIN 
                          ? 'bg-purple-100 text-purple-800' 
                          : user.role === ROLES.ADMIN 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {user.role === ROLES.SUPER_ADMIN ? 'Super Admin' : 
                         user.role === ROLES.ADMIN ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <select
                        value={user.role || ROLES.USER}
                        onChange={(e) => handleRoleChange(user.uid, e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        disabled={user.uid === auth.currentUser?.uid} // Prevent self-role change
                      >
                        <option value={ROLES.USER}>User</option>
                        <option value={ROLES.ADMIN}>Admin</option>
                        <option value={ROLES.SUPER_ADMIN}>Super Admin</option>
                      </select>
                      {user.uid === auth.currentUser?.uid && (
                        <p className="text-xs text-gray-500 mt-1">Cannot change own role</p>
                      )}
                    </td>
                    <td className="py-4 px-4 text-gray-600 text-sm">
                      {user.assignedAt ? new Date(user.assignedAt).toLocaleString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}